"use client";

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DevEnvironmentBadge } from "@/components/DevEnvironmentBadge";
import { BrandMark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useCoreAuth } from "@/lib/core-auth";

const loginSchema = z.object({
  email: z.string().email("Informe um e-mail válido"),
  password: z.string().min(1, "Informe a senha"),
});

type LoginValues = z.infer<typeof loginSchema>;


export default function EntrarPage() {
  return (
    <Suspense>
      <EntrarForm />
    </Suspense>
  );
}

function EntrarForm() {
  const { refresh } = useCoreAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const form = useForm<LoginValues>({ defaultValues: { email: "", password: "" } });
  const [apiError, setApiError] = useState<string | null>(null);
  const pendingLink = searchParams.get("erro") === "vinculo_pendente";
  const loginError =
    apiError ??
    (searchParams.get("erro") === "oidc" ? "Não foi possível entrar com Microsoft" : null);
  const [submitting, setSubmitting] = useState(false);
  const [oidc, setOidc] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/session/options", { credentials: "include", cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { oidc?: boolean } | null) => {
        if (!cancelled && body?.oidc === true) {
          setOidc(true);
        }
      })
      .catch(() => {
        /* local email/password only */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onLogin(values: LoginValues) {
    setApiError(null);
    const parsed = loginSchema.safeParse(values);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      if (issue?.path[0] === "email") {
        form.setError("email", { message: issue.message });
      } else if (issue?.path[0] === "password") {
        form.setError("password", { message: issue.message });
      }
      return;
    }
    form.clearErrors();
    setSubmitting(true);
    try {
      const res = await fetch("/api/v1/session/login", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ email: parsed.data.email, password: parsed.data.password }),
      });
      if (res.ok) {
        let loginBody: {
          password_must_change?: boolean;
          mfa_required?: boolean;
          mfa_enrollment_required?: boolean;
        } | null = null;
        try {
          loginBody = (await res.json()) as {
            password_must_change?: boolean;
            mfa_required?: boolean;
            mfa_enrollment_required?: boolean;
          };
        } catch {
          /* body is optional */
        }
        if (loginBody?.mfa_required) {
          router.push("/verificar-mfa");
          return;
        }
        const session = await refresh();
        if (!session) {
          setApiError("A sessão não ficou ativa no portal. Tente entrar de novo.");
          return;
        }
        if (session.password_must_change || loginBody?.password_must_change) {
          router.push("/trocar-senha");
          return;
        }
        if (session.mfa_enrollment_required || loginBody?.mfa_enrollment_required) {
          router.push("/cadastrar-mfa");
          return;
        }
        router.push("/app");
        return;
      }
      let message = "Não foi possível entrar";
      try {
        const body = (await res.json()) as { message?: string };
        if (body?.message) {
          message = body.message;
        }
      } catch {
        /* ignore non-JSON */
      }
      setApiError(message);
    } catch {
      setApiError("Não foi possível conectar. Tente novamente em instantes.");
    } finally {
      setSubmitting(false);
    }
  }


  return (
    <main className="login-shell grid lg:grid-cols-[minmax(280px,42%)_1fr]">
      <aside className="login-hero relative hidden overflow-hidden px-10 py-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="relative z-10">
          <BrandMark invert />
        </div>
        <div className="relative z-10 max-w-md space-y-4">
          <p className="text-xs font-semibold tracking-[0.18em] text-[#36A9E0] uppercase">
            Tecnologia e inteligência empresarial
          </p>
          <h1 className="text-4xl font-bold tracking-tight text-white">ASV Platform</h1>
          <p className="text-base leading-relaxed text-white/80">
            Conecte-se ao portal do Grupo ASV e encontre as ferramentas para o seu dia a dia.
          </p>
        </div>
        <p className="relative z-10 text-xs text-white/55">Grupo ASV · Portal de serviços</p>
      </aside>

      <section className="relative flex flex-col px-4 py-6 sm:px-8 sm:py-8 lg:px-12 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 lg:mb-8">
          <div className="lg:hidden">
            <BrandMark />
          </div>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-8">
          <header className="space-y-2">
            <DevEnvironmentBadge />
            <p className="text-xs font-semibold tracking-[0.16em] text-[var(--muted)] uppercase">
              Portal ASV
            </p>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Entrar</h2>
            <p className="text-sm leading-relaxed" style={{ color: "var(--muted)" }}>
              {oidc
                ? "Colaboradores podem entrar com Microsoft 365. Contas locais continuam com e-mail e senha."
                : "Acesse com seu e-mail e senha."}{" "}
              Se recebeu um código,{" "}
              <Link href="/aceitar-convite" className="underline">
                aceite o convite
              </Link>
              .
            </p>
          </header>

          <div className="login-card rounded-xl p-6 sm:p-8">
            {pendingLink ? (
              <p role="alert" className="notice error mb-6">
                O login com Microsoft encontrou um e-mail já cadastrado. Nenhuma sessão foi criada.
                Um administrador precisa aprovar o vínculo antes que você entre com essa conta.
              </p>
            ) : null}
            {searchParams.get("senha") === "redefinida" ? (
              <p role="status" className="notice success mb-6">
                Senha atualizada com sucesso. Suas outras sessões foram encerradas. Entre com sua nova senha.
              </p>
            ) : null}
            {oidc ? (
              <div className="mb-6 flex flex-col gap-4">
                {/* Full navigation so the browser follows the Core 302 and stores PKCE cookies. */}
                {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                <a
                  href="/api/v1/session/oidc/start"
                  className="btn-microsoft rounded-lg px-4 py-2.5 text-center text-sm font-semibold"
                >
                  Entrar com Microsoft
                </a>
                <p className="text-center text-xs" style={{ color: "var(--muted)" }}>
                  ou
                </p>
              </div>
            ) : null}
            <form method="post" className="flex flex-col gap-4" onSubmit={form.handleSubmit(onLogin)} noValidate>
              <div className="flex flex-col gap-2">
                <label htmlFor="login-email" className="text-sm font-semibold">
                  E-mail
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  placeholder="seu@email.com"
                  className="field-input rounded-lg px-3 py-2.5 text-sm"
                  {...form.register("email")}
                />
                {form.formState.errors.email ? (
                  <p role="alert" className="text-sm" style={{ color: "var(--warn)" }}>
                    {form.formState.errors.email.message}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="login-password" className="text-sm font-semibold">
                  Senha
                </label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  className="field-input rounded-lg px-3 py-2.5 text-sm"
                  {...form.register("password")}
                />
                {form.formState.errors.password ? (
                  <p role="alert" className="text-sm" style={{ color: "var(--warn)" }}>
                    {form.formState.errors.password.message}
                  </p>
                ) : null}
              </div>
              {loginError ? (
                <p role="alert" className="text-sm" style={{ color: "var(--warn)" }}>
                  {loginError}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
              >
                {submitting ? "Entrando…" : "Entrar"}
              </button>
              <Link href="/esqueci-senha" className="text-center text-sm underline">
                Esqueci minha senha
              </Link>
            </form>


          </div>
        </div>
      </section>
    </main>
  );
}
